import { queryClient } from "./queryClient";
import { auth } from "./firebase";

export interface Question {
  id: number;
  quizId: number;
  text: string;
  correctAnswer: "option-a" | "option-b";
  optionA: string;
  optionB: string;
}

export interface Quiz {
  id: number;
  internalId: string;
  title: string;
  welcomeMessage?: string;
  shareCode: string;
  creatorId?: number;
  questions: Question[];
}

export interface Score {
  id: number;
  quizId: number;
  playerName: string;
  score: number;
  createdAt: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) {
    return {};
  }

  // Get the Firebase ID token
  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

export async function createQuiz(quizData: {
  title: string;
  welcomeMessage?: string;
  questions: Array<{
    text: string;
    correctAnswer: "option-a" | "option-b";
    optionA: string;
    optionB: string;
  }>;
}): Promise<Quiz> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/quiz", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    } as HeadersInit,
    body: JSON.stringify(quizData),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  queryClient.invalidateQueries({ queryKey: ["/api/quiz/my"] });
  return response.json();
}

// Get quiz by internal ID (for management/editing)
export async function getQuiz(internalId: string): Promise<Quiz> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/quiz/${internalId}`, {
    headers: headers as HeadersInit,
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

// Get quiz by share code (for taking quizzes)
export async function getQuizByShareCode(shareCode: string): Promise<Quiz> {
  const response = await fetch(`/api/quiz/share/${shareCode}`);
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

export async function submitQuizAnswers(
  shareCode: string,
  data: {
    playerName: string;
    answers: string[];
  },
): Promise<Score> {
  const response = await fetch(`/api/quiz/submit/${shareCode}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  return response.json();
}

export async function updateQuiz(
  internalId: string,
  quizData: {
    title: string;
    welcomeMessage?: string;
    questions: Array<{
      text: string;
      correctAnswer: "option-a" | "option-b";
      optionA: string;
      optionB: string;
    }>;
  },
): Promise<Quiz> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/quiz/${internalId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    } as HeadersInit,
    body: JSON.stringify(quizData),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  queryClient.invalidateQueries({ queryKey: [`/api/quiz/${internalId}`] });
  queryClient.invalidateQueries({ queryKey: ["/api/quiz/my"] });
  return response.json();
}

export async function getUserQuizzes(): Promise<Quiz[]> {
  const headers = await getAuthHeaders();
  const response = await fetch("/api/quiz/my", {
    headers: headers as HeadersInit,
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

export async function deleteQuiz(internalId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/quiz/${internalId}`, {
    method: "DELETE",
    headers: headers as HeadersInit,
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  queryClient.invalidateQueries({ queryKey: [`/api/quiz/${internalId}`] });
  queryClient.invalidateQueries({ queryKey: ["/api/quiz/my"] });
}

export async function getQuizScores(internalId: string): Promise<Score[]> {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/quiz/${internalId}/scores`, {
    headers,
  });
  if (!response.ok) {
    throw new Error(await response.text());
  }
  return response.json();
}

export async function claimQuiz(internalId: string) {
  const headers = await getAuthHeaders();
  const response = await fetch(`/api/quiz/${internalId}/claim`, {
    method: "PUT",
    headers,
  });
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Server error response:", errorData);
    throw new Error(
      errorData.error || `Failed to claim quiz: ${response.status}`,
    );
  }

  return await response.json();
}
